/**
 * Union type representing all possible user roles in the barbershop rental platform
 * @typedef {'shop' | 'individual' | 'client' | 'admin' | null} UserRole
 */

/**
 * Enum representing the main user roles in the barbershop rental system
 * @constant {object}
 * @property {string} SHOP - Shop owner who manages a barbershop and its operations
 * @property {string} INDIVIDUAL - Individual barber or stylist providing services
 * @property {string} CLIENT - Customer who books appointments and services
 * @property {string} ADMIN - System administrator with full platform access
 * @example
 * // Check user role for authorization
 * if (userRole === USER_ROLES.SHOP) {
 *   // Allow shop management features
 * }
 */
export const USER_ROLES = {
  SHOP_OWNER: 'shop',
  INDIVIDUAL: 'individual',
  CLIENT: 'client',
  STAFF: 'staff',
  ADMIN: 'admin',
  null: null,
} as const;

export type UserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES];

/**
 * Type alias for user role values
 * @typedef {keyof typeof USER_ROLES} UserRoleType
 */
export type UserRoleType = UserRole;

/**
 * Enum representing member roles within a barbershop organization
 * Defines the hierarchy and permissions for shop team members
 * @constant {object}
 * @property {string} OWNER - Shop owner with full administrative rights
 * @property {string} MANAGER - Manager overseeing daily operations
 * @property {string} MEMBER - Regular shop member providing services
 * @property {string} RECEPTIONIST - Administrative staff handling bookings and customer service
 * @example
 * // Assign role to shop member
 * const memberData = {
 *   role: MEMBER_ROLES.MEMBER,
 *   permissions: getPermissionsByRole(MEMBER_ROLES.MEMBER)
 * };
 */
export const MEMBER_ROLES = {
  OWNER: 'owner',
  ADMIN: 'admin',
  MANAGER: 'manager',
  MEMBER: 'member',
  RECEPTIONIST: 'receptionist',
} as const;

/**
 * Type alias for member role values
 * @typedef {keyof typeof MEMBER_ROLES} MemberRoleType
 */
export type MemberRoleType = (typeof MEMBER_ROLES)[keyof typeof MEMBER_ROLES];

export const ALL_ROLES = {
  ...USER_ROLES,
  ...MEMBER_ROLES,
} as const;

export type AllRoles = (typeof ALL_ROLES)[keyof typeof ALL_ROLES];
